import { slideDown, slideUp } from '@globals/utils/functions';

/**
 * Class Tabs
 *
 * Handles all accordion and tab functionality as all tabs are accordions on mobile.
 */
class Tabs {
	transition: number;
	triggers: NodeListOf<HTMLElement>;
	tabs: NodeListOf<HTMLElement>;
	tabsBlock: HTMLElement;
	isAccordion: boolean;
	isInline: boolean;

	constructor(element: HTMLElement) {
		this.tabsBlock = element;
		this.isAccordion = this.tabsBlock.classList.contains('block-tabs--accordion');
		this.isInline = this.tabsBlock.classList.contains('block-tabs--inline');
		this.triggers = this.tabsBlock.querySelectorAll('.block-tab__top');
		this.tabs = this.tabsBlock.querySelectorAll('.block-tabs__tab');
		this.transition = 400;

		this.triggers.forEach((trigger) => {
			trigger.addEventListener('click', this.handleBellowClick.bind(this));
		});

		this.tabs.forEach((trigger) => {
			trigger.addEventListener('click', this.handleTabClick.bind(this));
		});

		if (!this.isAccordion) {
			this.setDefaultOpen();
			window.addEventListener('resize', this.setDefaultOpen.bind(this));
		}
	}

	/**
	 * Setup tabs if one tab is not set to be open by default.
	 */
	setDefaultOpen() {
		let hasExpanded = 0;

		this.triggers.forEach((trigger, index) => {
			if (trigger.closest('.block-tab')?.classList.contains('block-tab--expanded')) {
				hasExpanded = index;
			}
		});

		if (
			this.triggers[hasExpanded] &&
			this.tabs[hasExpanded] &&
			window.matchMedia(`(min-width: ${window.impBreakpoints?.md}px)`).matches
		) {
			this.expandTab(this.triggers[hasExpanded], this.tabs[hasExpanded]);
		} else if (this.isInline) {
			this.expandTab(this.triggers[hasExpanded], this.tabs[hasExpanded]);
		}
	}

	/**
	 * Handle bellow click
	 *
	 * @param {Event} event
	 */
	handleBellowClick(event: Event) {
		const target = event.currentTarget as HTMLElement;

		const isExpanded = target.closest('.block-tab')?.classList.contains('block-tab--expanded');

		if (isExpanded) {
			this.collapseBellow(target);
		} else {
			this.expandBellow(target);
		}
	}

	/**
	 * Collapse Bellow
	 *
	 * @param {HTMLElement} element Bellow to collapse
	 */
	collapseBellow(element: HTMLElement) {
		const bellowTrigger = element as HTMLElement;

		bellowTrigger.setAttribute('aria-expanded', 'false');
		const bellowElement = bellowTrigger.closest('.block-tab');
		if (bellowElement) {
			bellowElement.classList.remove('block-tab--expanded');
			const collapsable = bellowElement.querySelector<HTMLElement>('.block-tab__collapsable');
			if (collapsable) {
				slideUp(collapsable);
			}
		}
	}

	/**
	 * Expand Bellow
	 *
	 * @param {HTMLElement} element Bellow to expand
	 */
	expandBellow(element: HTMLElement) {
		// Expand bellow.
		const bellowTrigger = element as HTMLElement;
		bellowTrigger.setAttribute('aria-expanded', 'true');
		const bellowElement = bellowTrigger.closest('.block-tab');
		if (bellowElement) {
			bellowElement.classList.add('block-tab--expanded');
			const collapsable = bellowElement.querySelector<HTMLElement>('.block-tab__collapsable');
			if (collapsable) {
				slideDown(collapsable);
			}
		}
	}

	/**
	 * Handle tab click
	 *
	 * @param {Event} event
	 */
	handleTabClick(event: Event) {
		const tab = event.currentTarget as HTMLElement;
		const targetID = tab.getAttribute('aria-controls');
		if (targetID) {
			const target = this.tabsBlock.querySelector(`#${targetID}`) as HTMLElement;
			const isExpanded = target
				.closest('.block-tab')
				?.classList.contains('block-tab--expanded');

			if (!isExpanded) {
				this.expandTab(target, tab);
			}
		}
	}

	/**
	 * Expand Tab
	 *
	 * @param {HTMLElement} element Tab block to expand
	 * @param {HTMLElement} tab     External tab trigger
	 */
	expandTab(element: HTMLElement, tab: HTMLElement) {
		// Collapse other tabs.
		this.tabs.forEach((trigger) => {
			trigger.classList.remove('block-tabs__tab--expanded');
			trigger.setAttribute('aria-expanded', 'false');
		});
		tab.classList.add('block-tabs__tab--expanded');
		tab.setAttribute('aria-expanded', 'true');

		const expandedTabs = this.tabsBlock.querySelectorAll(
			'.block-tab--expanded .block-tab__top'
		);
		expandedTabs.forEach((expandedTab) => {
			this.collapseTab(expandedTab);
		});

		// Expand tab.
		const tabTrigger = element as HTMLElement;
		tabTrigger.setAttribute('aria-expanded', 'true');
		const tabElement = tabTrigger.closest('.block-tab');
		if (tabElement) {
			tabElement.classList.add('block-tab--expanded');
			const collapsable = tabElement.querySelector<HTMLElement>('.block-tab__collapsable');
			if (collapsable) {
				collapsable.style.display = 'block';
			}
		}
	}

	/**
	 * Collapse Tab
	 *
	 * @param {HTMLElement} element Tab block to collapse
	 */
	collapseTab(element: Element) {
		const tabTrigger = element as HTMLElement;

		tabTrigger.setAttribute('aria-expanded', 'false');
		const tabElement = tabTrigger.closest('.block-tab');
		if (tabElement) {
			tabElement.classList.remove('block-tab--expanded');
			const collapsable = tabElement.querySelector<HTMLElement>('.block-tab__collapsable');
			if (collapsable) {
				collapsable.style.display = 'none';
			}
		}
	}
}

// Initialize the tabs on all elements with class 'block-tabs'
document.querySelectorAll('.block-tabs').forEach((tabs) => {
	new Tabs(tabs as HTMLElement);
});
